import {
  Box,
  Card,
  Flex,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
  useComputedColorScheme,
} from "@mantine/core";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ContinuousPerformanceStageEvent,
  Stimulus,
  StimulusResponse,
  StimulusResponseState,
  StimulusType,
} from "types/tests";
import { StageBucket, buildStageBuckets, renderStageDisplayName } from "../utils/cptStageMetrics";

interface CPTTrialChipStripProps {
  stageEvents?: ContinuousPerformanceStageEvent[];
  stimuli?: Stimulus[] | null;
  responses?: StimulusResponse[] | null;
}

interface TrialOutcomeCategory {
  key: string;
  labelKey: string;
  color: string;
  states: StimulusResponseState[];
}

const CATEGORY_KEY_PREFIX = "extraTests.continuousPerformance.result.trialChip.categories";
const STATE_KEY_PREFIX = "extraTests.continuousPerformance.result.trialChip.states";

const CATEGORIES: TrialOutcomeCategory[] = [
  {
    key: "correct",
    labelKey: `${CATEGORY_KEY_PREFIX}.correct`,
    color: "teal.7",
    states: [StimulusResponseState.Correct],
  },
  {
    key: "correctMultiPress",
    labelKey: `${CATEGORY_KEY_PREFIX}.correctMultiPress`,
    color: "teal.3",
    states: [StimulusResponseState.CorrectMultiPress],
  },
  {
    key: "miss",
    labelKey: `${CATEGORY_KEY_PREFIX}.miss`,
    color: "orange.6",
    states: [StimulusResponseState.Miss],
  },
  {
    key: "incorrect",
    labelKey: `${CATEGORY_KEY_PREFIX}.incorrect`,
    color: "grape.7",
    states: [StimulusResponseState.Incorrect, StimulusResponseState.IncorrectMultiPress],
  },
  {
    key: "falsePositive",
    labelKey: `${CATEGORY_KEY_PREFIX}.falsePositive`,
    color: "grape.9",
    states: [
      StimulusResponseState.FalsePositiveEmpty,
      StimulusResponseState.FalsePositiveAudio,
      StimulusResponseState.FalsePositiveVisual,
      StimulusResponseState.FalsePositiveAudioVisual,
    ],
  },
];

function categoryForState(state: StimulusResponseState): TrialOutcomeCategory | undefined {
  return CATEGORIES.find((c) => c.states.includes(state));
}

function stateKey(state: StimulusResponseState): string {
  switch (state) {
    case StimulusResponseState.Correct:
      return `${STATE_KEY_PREFIX}.correct`;
    case StimulusResponseState.CorrectMultiPress:
      return `${STATE_KEY_PREFIX}.correctMultiPress`;
    case StimulusResponseState.Incorrect:
      return `${STATE_KEY_PREFIX}.incorrect`;
    case StimulusResponseState.IncorrectMultiPress:
      return `${STATE_KEY_PREFIX}.incorrectMultiPress`;
    case StimulusResponseState.Miss:
      return `${STATE_KEY_PREFIX}.miss`;
    case StimulusResponseState.FalsePositiveEmpty:
      return `${STATE_KEY_PREFIX}.falsePositiveEmpty`;
    case StimulusResponseState.FalsePositiveAudio:
      return `${STATE_KEY_PREFIX}.falsePositiveAudio`;
    case StimulusResponseState.FalsePositiveVisual:
      return `${STATE_KEY_PREFIX}.falsePositiveVisual`;
    case StimulusResponseState.FalsePositiveAudioVisual:
      return `${STATE_KEY_PREFIX}.falsePositiveAudioVisual`;
    default:
      return String(state);
  }
}

const STIMULUS_TYPE_KEY_PREFIX = "extraTests.continuousPerformance.result.trialChip.stimulusTypes";

function stimulusTypeLabel(type: StimulusType, t: TFunction): string {
  return t(`${STIMULUS_TYPE_KEY_PREFIX}.${type}`, { defaultValue: String(type) });
}

interface PartitionedRow {
  stage: StageBucket;
  responses: StimulusResponse[];
}

function partitionResponses(
  buckets: StageBucket[],
  responses: StimulusResponse[] | undefined | null,
): PartitionedRow[] {
  if (!buckets.length) return [];
  const sorted = [...(responses ?? [])].sort((a, b) => a.timeStamp - b.timeStamp);
  return buckets.map((stage) => ({
    stage,
    responses: sorted.filter(
      (r) =>
        !stage.empty &&
        Number.isFinite(r.timeStamp) &&
        r.timeStamp >= stage.startTime &&
        r.timeStamp < stage.endTime,
    ),
  }));
}

function findStimulusFor(
  stimuli: Stimulus[] | undefined | null,
  responseTime: number,
): Stimulus | undefined {
  if (!stimuli?.length) return undefined;
  const exact = stimuli.find((s) => s.timeStamp === responseTime);
  if (exact) return exact;
  let best: { stim: Stimulus; delta: number } | undefined;
  for (const s of stimuli) {
    const d = Math.abs((s.timeStamp ?? 0) - responseTime);
    if (d > 0.1) continue;
    if (!best || d < best.delta) best = { stim: s, delta: d };
  }
  return best?.stim;
}

const TrialChip: React.FC<{
  trialIndex: number;
  response: StimulusResponse;
  stimulus?: Stimulus;
  t: TFunction;
}> = ({ trialIndex, response, stimulus, t }) => {
  const category = categoryForState(response.state);
  const color = category?.color ?? "gray.4";
  const stateLabel = t(stateKey(response.state));
  const tt = "extraTests.continuousPerformance.result.trialChip.tooltip";

  const tooltipLines = [
    t(`${tt}.trial`, { index: trialIndex + 1 }),
    t(`${tt}.outcome`, { state: stateLabel }),
    stimulus
      ? stimulus.id
        ? t(`${tt}.stimulusWithId`, { type: stimulusTypeLabel(stimulus.type, t), id: stimulus.id })
        : t(`${tt}.stimulus`, { type: stimulusTypeLabel(stimulus.type, t) })
      : null,
    t(`${tt}.time`, { time: response.timeStamp.toFixed(2) }),
    Number.isFinite(response.responseDelta)
      ? t(`${tt}.rt`, { rt: Math.round(response.responseDelta) })
      : null,
  ].filter(Boolean) as string[];

  return (
    <Tooltip
      label={
        <Stack gap={2}>
          {tooltipLines.map((l, i) => (
            <Text key={i} size="xs">
              {l}
            </Text>
          ))}
        </Stack>
      }
      withArrow
      openDelay={150}
      position="top"
    >
      <Box
        role="img"
        aria-label={t("extraTests.continuousPerformance.result.trialChip.ariaLabel", {
          index: trialIndex + 1,
          state: stateLabel,
        })}
        style={{
          width: "100%",
          height: 18,
          minWidth: 2,
          borderRadius: 2,
          backgroundColor: `var(--mantine-color-${color.replace(".", "-")})`,
        }}
      />
    </Tooltip>
  );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Group gap={6} wrap="nowrap">
    <Box
      style={{
        width: 10,
        height: 10,
        borderRadius: 2,
        backgroundColor: `var(--mantine-color-${color.replace(".", "-")})`,
      }}
    />
    <Text size="xs" c="dimmed">
      {label}
    </Text>
  </Group>
);

const CPTTrialChipStrip: React.FC<CPTTrialChipStripProps> = ({
  stageEvents,
  stimuli,
  responses,
}) => {
  const { t } = useTranslation();
  const scheme = useComputedColorScheme("light");
  const dividerColor =
    scheme === "dark" ? "var(--mantine-color-dark-4)" : "var(--mantine-color-gray-3)";
  const buckets = useMemo(() => buildStageBuckets(stageEvents), [stageEvents]);
  const rows = useMemo(() => partitionResponses(buckets, responses), [buckets, responses]);

  if (!buckets.length || !rows.some((r) => r.responses.length)) {
    return (
      <Card withBorder radius="md" p="lg">
        <Text c="dimmed">{t("extraTests.continuousPerformance.result.trialChip.noResponses")}</Text>
      </Card>
    );
  }

  const maxTrialsInRow = Math.max(...rows.map((r) => r.responses.length), 1);
  let runningIndex = 0;

  return (
    <Card withBorder radius="md" p="lg">
      <Stack gap="md">
        <Box>
          <Title order={5} mb={4}>
            {t("extraTests.continuousPerformance.result.trialChip.title")}
          </Title>
          <Text size="sm" c="dimmed" lh={1.4}>
            {t("extraTests.continuousPerformance.result.trialChip.description")}
          </Text>
        </Box>

        <Stack gap="sm">
          {rows.map(({ stage, responses: stageResponses }, rowIndex) => {
            const startIndex = runningIndex;
            runningIndex += stageResponses.length;
            return (
              <Flex
                key={stage.key}
                gap="md"
                align="flex-start"
                wrap="nowrap"
                style={{
                  paddingBottom: rowIndex < rows.length - 1 ? 8 : 0,
                  borderBottom: rowIndex < rows.length - 1 ? `1px dashed ${dividerColor}` : "none",
                  breakInside: "avoid",
                  pageBreakInside: "avoid",
                }}
              >
                <Stack gap={0} miw={140}>
                  <Text fw={700} size="sm">
                    {renderStageDisplayName(stage, t)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {stage.shortLabel}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {t("extraTests.continuousPerformance.result.trialChip.trials", {
                      count: stageResponses.length,
                    })}
                  </Text>
                </Stack>
                <Box
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "grid",
                    gridTemplateColumns: `repeat(${maxTrialsInRow}, 1fr)`,
                    gap: 3,
                    alignItems: "center",
                  }}
                >
                  {stageResponses.length === 0 ? (
                    <Text size="xs" c="dimmed" style={{ gridColumn: `1 / span ${maxTrialsInRow}` }}>
                      {t("extraTests.continuousPerformance.result.trialChip.noOutcomesInWindow")}
                    </Text>
                  ) : (
                    stageResponses.map((response, j) => (
                      <TrialChip
                        key={`${stage.key}-${j}`}
                        trialIndex={startIndex + j}
                        response={response}
                        stimulus={findStimulusFor(stimuli, response.timeStamp)}
                        t={t}
                      />
                    ))
                  )}
                </Box>
              </Flex>
            );
          })}
        </Stack>

        <Group gap="lg" wrap="wrap">
          {CATEGORIES.map((c) => (
            <LegendDot key={c.key} color={c.color} label={t(c.labelKey)} />
          ))}
        </Group>
      </Stack>
    </Card>
  );
};

export default CPTTrialChipStrip;
